import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export function LazyImage({
  src,
  className,
  imageClassName,
  imageStyle,
  onImageLoaded,
}: {
  src: string;
  className?: string;
  imageClassName?: string;
  imageStyle?: React.CSSProperties;
  onImageLoaded?: (dimensions: { width: number; height: number }) => void;
}) {
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [prevSrc, setPrevSrc] = useState(src);
  const ref = useRef<HTMLAnchorElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Reset load state when the source changes so a new URL re-enters the loading
  // flow rather than showing the previous image's loaded/error result.
  if (src !== prevSrc) {
    setPrevSrc(src);
    setLoaded(false);
    setError(false);
  }

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect(); // Only trigger once
        }
      },
      {
        rootMargin: '100px', // start loading slightly before entering view
        threshold: 0.01,
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    preloadImage(src)
      .then(() => {
        if (!cancelled) setLoaded(true);
      })
      // Without this the promise rejects unhandled and `loaded` stays false,
      // leaving the spinner running forever on any image that fails to load.
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [src, visible]);

  return (
    <a ref={ref} className={twMerge('bg-white dark:bg-zinc-800', className)} style={{ width: '100%', height: '100%' }}>
      {error ? (
        <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 dark:text-zinc-500">
          Image unavailable
        </div>
      ) : loaded ? (
        <img
          ref={imageRef}
          src={src}
          alt="Image"
          className={twMerge('mx-auto h-full object-contain bg-white', imageClassName)}
          style={imageStyle}
          onLoad={(e) => {
            if (e.target instanceof HTMLImageElement) {
              onImageLoaded?.({ width: e.target.width, height: e.target.height });
            }
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
        </div>
      )}
    </a>
  );
}

function preloadImage(src: string) {
  return new Promise<unknown>((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = resolve;
    img.onerror = reject;
  });
}
